import { isValidHex, hexToHsl, adjustLightness, getContrastForeground } from './color-utils';

const CACHE_KEY = 'brand-accent-color';

/** CSS variable names that use the primary/accent color */
const BRAND_VARS = [
  '--primary',
  '--primary-foreground',
  '--accent',
  '--accent-foreground',
  '--ring',
  '--sidebar-primary',
  '--sidebar-primary-foreground',
  '--sidebar-ring',
  '--chart-1',
  '--chart-2',
  '--chart-3',
  '--chart-4',
  '--chart-5',
] as const;

/**
 * Apply a brand accent color by setting CSS custom properties on the document root.
 * Inline styles override both light and dark mode defaults.
 */
export function applyBrandTheme(hex: string): void {
  if (!isValidHex(hex)) return;

  try { localStorage.setItem(CACHE_KEY, hex); } catch { /* noop */ }

  const root = document.documentElement;
  const baseHsl = hexToHsl(hex);
  const fg = getContrastForeground(hex);

  // Light mode — use the color as-is
  root.style.setProperty('--primary', baseHsl);
  root.style.setProperty('--accent', baseHsl);
  root.style.setProperty('--ring', baseHsl);
  root.style.setProperty('--sidebar-primary', baseHsl);
  root.style.setProperty('--sidebar-ring', baseHsl);
  root.style.setProperty('--primary-foreground', fg);
  root.style.setProperty('--accent-foreground', fg);
  root.style.setProperty('--sidebar-primary-foreground', fg);

  // Chart colors — step lightness up from base
  root.style.setProperty('--chart-1', baseHsl);
  root.style.setProperty('--chart-2', adjustLightness(baseHsl, 13));
  root.style.setProperty('--chart-3', adjustLightness(baseHsl, 26));
  root.style.setProperty('--chart-4', adjustLightness(baseHsl, 39));
  root.style.setProperty('--chart-5', adjustLightness(baseHsl, 52));
}

/** Remove all brand-set CSS custom properties so defaults take effect. */
export function clearBrandTheme(): void {
  const root = document.documentElement;
  for (const v of BRAND_VARS) {
    root.style.removeProperty(v);
  }
  try { localStorage.removeItem(CACHE_KEY); } catch { /* noop */ }
}

/** Apply cached brand theme immediately (call before React mounts to prevent flicker). */
export function restoreBrandTheme(): void {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) applyBrandTheme(cached);
  } catch { /* noop */ }
}
